'use client';

import { useMemo, useState } from 'react';
import {
  convertBase,
  getBaseConverterConfig,
  type BaseConverterVariant,
} from '@/lib/browser-base-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserBaseConverter({ variant }: { variant: BaseConverterVariant }) {
  const config = useMemo(() => getBaseConverterConfig(variant), [variant]);
  const [input, setInput] = useState(config.sample);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  let output = '';
  let errorMessage: string | null = null;
  try {
    output = convertBase(config, input);
  } catch (error) {
    errorMessage = error instanceof Error ? error.message : 'This value could not be converted.';
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Result copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">
          {config.fromLabel} → {config.toLabel}
        </span>
        <h2 className="tool-live-title">
          Convert {config.fromLabel.toLowerCase()} to {config.toLabel.toLowerCase()}
        </h2>
        <p className="tool-live-subtitle">
          Type or paste your input and the result updates live in your browser. Nothing is uploaded
          and no server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>{config.fromLabel} input</span>
            <textarea
              onChange={(event) => {
                setCopyMessage(null);
                setInput(event.target.value);
              }}
              placeholder={config.sample}
              rows={5}
              value={input}
            />
          </label>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => {
                setCopyMessage(null);
                setInput('');
              }}
              type="button"
            >
              Clear
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>

          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>{config.toLabel} output</span>
            <textarea readOnly rows={5} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Conversion runs locally in your browser. Whitespace-separated values are converted in
            batch.
          </p>
        </section>
      </div>
    </div>
  );
}
